import api from './api';

export interface Payment {
  id: string;
  transactionId: string;
  customerId: string;
  customerName?: string;
  entityId: string;
  entityName?: string;
  amount: number;
  fee: number;
  netAmount: number;
  currency: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded';
  paymentMethod: string;
  description?: string;
  reference?: string;
  createdAt: Date | string;
  completedAt?: Date | string;
}

export interface PaymentFilters {
  search?: string;
  status?: string;
  entityId?: string;
  entidade?: string;
  servico?: string;
  evento?: string;
  customerId?: string;
  startDate?: string;
  endDate?: string;
  paymentMethod?: string;
  source?: string;
  minAmount?: number;
  maxAmount?: number;
  page?: number;
  limit?: number;
}

export interface PaymentsResponse {
  success: boolean;
  data: Payment[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  summary?: {
    totalAmount: number;
    totalFees: number;
    totalNet: number;
  };
}

class PaymentsService {
  /**
   * Get all payments with filters
   */
  async getPayments(filters?: PaymentFilters): Promise<PaymentsResponse> {
    try {
      const params = new URLSearchParams();
      if (filters?.search) params.append('search', filters.search);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.entityId) params.append('entityId', filters.entityId);
      if (filters?.entidade) params.append('entidade', filters.entidade);
      if (filters?.servico) params.append('servico', filters.servico);
      if (filters?.evento) params.append('evento', filters.evento);
      if (filters?.customerId) params.append('customerId', filters.customerId);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.paymentMethod) params.append('paymentMethod', filters.paymentMethod);
      if (filters?.source) params.append('source', filters.source);
      if (filters?.minAmount != null) params.append('minAmount', String(filters.minAmount));
      if (filters?.maxAmount != null) params.append('maxAmount', String(filters.maxAmount));
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const response = await api.get(`/admin/payments?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get payments error:', error);
      throw error;
    }
  }

  /**
   * Get payment by ID
   */
  async getPayment(id: string): Promise<Payment> {
    try {
      const response = await api.get(`/admin/payments/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Get payment error:', error);
      throw error;
    }
  }

  /**
   * Get payment stats (real totals from backend)
   */
  async getStats(period: number = 30): Promise<any> {
    try {
      const response = await api.get(`/admin/payments/stats?period=${period}`);
      return response.data.data;
    } catch (error) {
      console.error('Get payment stats error:', error);
      return null;
    }
  }

  /**
   * Opções para os filtros das listagens (entidades e serviços).
   */
  /** Pagamentos aprovados pela SISP que NÃO pagaram a factura (cobrados como compra). */
  async getFacturasNaoPagas(): Promise<Array<{ id: string; reference: string; createdAt: string; customerName?: string; invoiceReference: string; amount: number; entityCodeSent?: string | null }>> {
    try {
      const response = await api.get(`/admin/payments/facturas-nao-pagas`);
      return response.data?.data || [];
    } catch {
      return [];
    }
  }

  async resolverFacturaNaoPaga(ref: string, nota: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await api.post(`/admin/payments/facturas-nao-pagas/${encodeURIComponent(ref)}/resolver`, { nota });
      return response.data;
    } catch (e: any) {
      return { success: false, error: e?.response?.data?.error || 'Erro ao registar' };
    }
  }

  async getFilterOptions(): Promise<{ entidades: string[]; servicos: { codigo: string; label: string; total?: number }[]; eventos: { id: string; label: string }[] }> {
    try {
      const response = await api.get(`/admin/payments/filter-options`);
      return { eventos: [], ...(response.data.data || { entidades: [], servicos: [] }) };
    } catch (error) {
      console.error('Get payment filter options error:', error);
      return { entidades: [], servicos: [], eventos: [] };
    }
  }

  /**
   * Refund payment
   */
  async refundPayment(id: string, reason?: string): Promise<Payment> {
    try {
      const response = await api.post(`/admin/payments/${id}/refund`, { reason });
      return response.data.data;
    } catch (error) {
      console.error('Refund payment error:', error);
      throw error;
    }
  }

  /**
   * Cancel payment
   */
  async cancelPayment(id: string, reason?: string): Promise<Payment> {
    try {
      const response = await api.post(`/admin/payments/${id}/cancel`, { reason });
      return response.data.data;
    } catch (error) {
      console.error('Cancel payment error:', error);
      throw error;
    }
  }

  /**
   * Export payments to CSV
   */
  async exportPayments(filters?: PaymentFilters): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      if (filters?.search) params.append('search', filters.search);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.entityId) params.append('entityId', filters.entityId);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);

      const response = await api.get(`/admin/payments/export?${params.toString()}`, {
        responseType: 'blob',
      });

      return response.data;
    } catch (error) {
      console.error('Export payments error:', error);
      throw error;
    }
  }

  /**
   * Get real-time SISP payments
   */
  async getRealTimePayments(filters?: {
    page?: number;
    limit?: number;
    status?: string;
    entity?: string;
    search?: string;
  }): Promise<{
    data: RealTimePayment[];
    stats: RealTimeStats;
    pagination: { page: number; limit: number; total: number };
  }> {
    try {
      const params = new URLSearchParams();
      if (filters?.page) params.append('page', String(filters.page));
      if (filters?.limit) params.append('limit', String(filters.limit));
      if (filters?.status) params.append('status', filters.status);
      if (filters?.entity) params.append('entity', filters.entity);
      if (filters?.search) params.append('search', filters.search);

      const response = await api.get(`/admin/payments/realtime?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get realtime payments error:', error);
      throw error;
    }
  }

  /**
   * Pagamentos com cartão guardado (tokenização SISP) — só leitura.
   */
  async getTokenPayments(filters?: {
    page?: number;
    limit?: number;
    status?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
  }): Promise<TokenPaymentsResponse> {
    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== '') params.append(k, String(v));
    });
    const response = await api.get(`/admin/payments/tokens?${params.toString()}`);
    return response.data;
  }

  /**
   * Reenviar email de bilhetes de evento para um pedido.
   * orderId pode ser o ID numérico ou o código do pedido (ex: "PED-0123").
   */
  async resendTicketEmail(orderId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.post(`/admin/events/orders/${encodeURIComponent(orderId)}/resend-email`);
      return response.data;
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.message || 'Erro ao reenviar email';
      throw new Error(msg);
    }
  }
}

export interface RealTimePayment {
  id: string;
  reference: string;
  entity: string;
  entityCode: string;
  amount: number;
  status: 'pending' | 'processing' | 'success' | 'failed';
  paymentMethod: string;
  clientName: string;
  clientPhone: string;
  timestamp: string;
  transactionId?: string;
  terminalId?: string;
  errorMessage?: string;
}

export interface RealTimeStats {
  total: number;
  success: number;
  pending: number;
  failed: number;
  processing: number;
  totalAmount: number;
}

export interface TokenPayment {
  id: string;
  reference: string;
  amount: number;
  status: 'PAID' | 'FAILED' | 'PENDING' | 'REFUNDED' | string;
  createdAt: string;
  origin: string | null;
  tipoServico: string | null;
  serviceReference: string | null;
  transactionCode: string | null;
  entityName: string | null;
  serviceLabel?: string;
  customerName: string | null;
  customerEmail: string | null;
  cardLast4: string;
  cardType: string;
  cardHolder: string;
}

export interface TokenPaymentsResponse {
  data: TokenPayment[];
  stats: { total: number; paid: number; failed: number; totalPaid: number };
  pagination: { page: number; limit: number; total: number };
}

export default new PaymentsService();
